import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { ResourceExplorerGridDataWithId } from "@/store/resource-explorer/utils/types/resourceExplorerDataGrid";
import {
  ForecastedCost,
  ForecastOption,
  ResourceExplorerCostsDateDetails,
  ResourceExplorerGroupingNullable,
  WidgetCostBreakdownVisualization,
} from "@/services/cloudchipr.api";
import { BillingExplorerWidgetLabelCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/cells/BillingExplorerWidgetLabelCell";
import {
  billingExplorerWidgetLabelValueGetter,
  billingExplorerWidgetPrevCostValueGetter,
  billingExplorerWidgetTotalCostValueGetter,
  billingExplorerWidgetTrendValueGetter,
  getBillingExplorerWidgetForecastedCostValueGetter,
} from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/utils/helpers/valueGetters";

import { BillingExplorerWidgetLabelHeaderCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/header-cells/BillingExplorerWidgetLabelHeaderCell";
import { getSimpleNumberComparatorFnByField } from "@/components/pages/common/ag-grid/utils/helpers/sortingFunctions/simpleNumberComparatorFnByField";
import { BillingExplorerWidgetCostHeaderCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/header-cells/BillingExplorerWidgetCostHeaderCell";
import { BillingExplorerWidgetSimpleCellRenderer } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/cells/BillingExplorerWidgetSimpleCellRenderer";
import { BillingExplorerWidgetPrevCostCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/cells/BillingExplorerWidgetPrevCostCell";
import { BillingExplorerWidgetTrendHeaderCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/header-cells/BillingExplorerWidgetTrendHeaderCell";
import { BillingExplorerWidgetTrendCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/cells/BillingExplorerWidgetTrendCell";
import { groupingsExcludingForecastedCostsColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/utils/constants/groupingsExcludingForecastedCostsColumns";
import { forecastedCostOptionLabels } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/resource-explorer-metadata/components/utils/constants";
import { BillingExplorerWidgetForecastedCostHeaderCell } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/header-cells/BillingExplorerWidgetForecastedCostHeaderCell";

import { agGridEmptyColumnToFitEmptySpace } from "@/components/pages/common/ag-grid/utils/constants/agGridEmptyColumnToFitEmptySpace";

interface GetBillingExplorerWidgetAgGridColumnDefsArgs {
  totalItemsCount?: number;
  totalTrend: number | null;
  forecastedCost?: ForecastedCost;
  forecastOption?: ForecastOption;
  grouping?: ResourceExplorerGroupingNullable;
  visualization?: WidgetCostBreakdownVisualization;
  prevCostDates?: ResourceExplorerCostsDateDetails;
  totalCostDates?: ResourceExplorerCostsDateDetails;
}

export const billingExplorerWidgetTotalCostColId = "total_cost";
export const billingExplorerWidgetPrevCostColId = "previous_period_total_cost";
export const billingExplorerWidgetForecastedMonthlyCostColId =
  "monthly_forecast.cost";
export const billingExplorerWidgetForecastedQuarterlyCostColId =
  "quarterly_forecast.cost";

export const getBillingExplorerWidgetAgGridColumnDefs = ({
  grouping,
  totalTrend,
  visualization,
  prevCostDates,
  forecastedCost,
  forecastOption,
  totalCostDates,
  totalItemsCount,
}: GetBillingExplorerWidgetAgGridColumnDefsArgs): ColDef<ResourceExplorerGridDataWithId>[] => {
  const columns: ColDef<ResourceExplorerGridDataWithId>[] = [
    {
      flex: 9,
      width: 400,
      field: "label",
      headerComponentParams: {
        innerHeaderComponent: BillingExplorerWidgetLabelHeaderCell,
        innerHeaderComponentParams: {
          grouping,
          totalItemsCount,
        },
      },
      cellRenderer: BillingExplorerWidgetLabelCell,
      valueGetter: billingExplorerWidgetLabelValueGetter,
    },
    {
      flex: 4,
      field: billingExplorerWidgetTotalCostColId,
      headerName: "Total Cost",
      headerComponentParams: {
        innerHeaderComponent: BillingExplorerWidgetCostHeaderCell,
        innerHeaderComponentParams: {
          dates: totalCostDates,
        },
      },
      cellRenderer: BillingExplorerWidgetSimpleCellRenderer,
      valueGetter: billingExplorerWidgetTotalCostValueGetter,
      comparator: getSimpleNumberComparatorFnByField(
        billingExplorerWidgetTotalCostColId,
      ),
    },
  ];

  if (visualization?.previous_period_cost) {
    columns.push({
      flex: 5,
      minWidth: 250,
      field: billingExplorerWidgetPrevCostColId,
      headerName: "Previous Period Total Cost",
      headerComponentParams: {
        innerHeaderComponent: BillingExplorerWidgetCostHeaderCell,
        innerHeaderComponentParams: {
          dates: prevCostDates,
        },
      },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<ResourceExplorerGridDataWithId>) => (
        <BillingExplorerWidgetPrevCostCell
          cost={data?.previous_period_total_cost}
          dates={prevCostDates}
        />
      ),
      valueGetter: billingExplorerWidgetPrevCostValueGetter,
      comparator: getSimpleNumberComparatorFnByField(
        billingExplorerWidgetPrevCostColId,
      ),
    } as ColDef<ResourceExplorerGridDataWithId>);
  }

  if (visualization?.trend) {
    columns.push({
      field: "trend",
      headerComponentParams: {
        innerHeaderComponent: BillingExplorerWidgetTrendHeaderCell,
        innerHeaderComponentParams: {
          trend: totalTrend,
        },
      },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<ResourceExplorerGridDataWithId>) => (
        <BillingExplorerWidgetTrendCell
          trend={data?.trend}
          dates={prevCostDates}
        />
      ),
      valueGetter: billingExplorerWidgetTrendValueGetter,
      comparator: getSimpleNumberComparatorFnByField("trend"),
    });
  }

  if (
    grouping &&
    forecastOption &&
    visualization?.forecasted_cost &&
    !groupingsExcludingForecastedCostsColumns.has(grouping)
  ) {
    const field =
      forecastOption === "month"
        ? billingExplorerWidgetForecastedMonthlyCostColId
        : billingExplorerWidgetForecastedQuarterlyCostColId;

    columns.push({
      flex: 5.5,
      minWidth: 250,
      field,
      headerName: `Forecasted ${forecastedCostOptionLabels.get(forecastOption)} Cost`,
      headerComponentParams: {
        innerHeaderComponent: BillingExplorerWidgetForecastedCostHeaderCell,
        innerHeaderComponentParams: {
          forecastedCost: forecastedCost,
          forecastOption: forecastOption,
        },
      },
      cellRenderer: BillingExplorerWidgetSimpleCellRenderer,
      valueGetter:
        getBillingExplorerWidgetForecastedCostValueGetter(forecastOption),
      comparator: getSimpleNumberComparatorFnByField(field),
    });
  }

  columns.push(agGridEmptyColumnToFitEmptySpace);

  return columns;
};
